import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState, type JSX } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';

import { AppDialog } from './AppDialog';
import { entryApi, EntryApiError } from '../api/entryApi';
import { useEntryStore } from '../store/useEntryStore';

// Paths where the Age Gate modal must NOT appear (user must be free to read
// legal documents and the access-denied/age-restricted pages without being
// blocked).
const PUBLIC_LEGAL_PATHS = new Set(['/legal', '/tos', '/privacy', '/access-denied', '/age-restricted', '/404']);

interface CheckboxState {
  age: boolean;
  tos: boolean;
  privacy: boolean;
}

export function AgeGateModal(): JSX.Element | null {
  const data = useEntryStore((s) => s.data);
  const refetch = useEntryStore((s) => s.refetch);
  const navigate = useNavigate();
  const location = useLocation();
  const [checks, setChecks] = useState<CheckboxState>({ age: false, tos: false, privacy: false });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Show only when entry orchestrator says so AND user is NOT on a public legal page.
  if (!data || !data.age_gate.required) return null;
  if (data.blacklist.status === 'BLOCKED') return null;
  if (PUBLIC_LEGAL_PATHS.has(location.pathname)) return null;

  const allChecked = checks.age && checks.tos && checks.privacy;

  const handleSubmit = async (): Promise<void> => {
    if (!allChecked) return;
    setSubmitting(true);
    setError(null);
    try {
      await entryApi.submitAgeGate({ terms_version: data.age_gate.terms_version });
      await refetch();
    } catch (err) {
      if (err instanceof EntryApiError) {
        if (err.code === 'cookies_required') {
          setError('Please enable cookies to continue. We use them for session and security.');
        } else if (err.code === 'terms_version_mismatch') {
          setError('The terms have been updated. The page will refresh — please re-accept.');
          await refetch();
        } else {
          setError(err.message);
        }
      } else {
        setError('Could not complete. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppDialog
      id="age-gate"
      open
      onClose={() => {
        // Mandatory modal — no close path. Provide under-18 link instead.
      }}
      closeOnEscape={false}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Confirm before you continue</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            To use this service you must be an adult and accept our terms.
          </Typography>

          <Stack spacing={0.5}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={checks.age}
                  onChange={(_, v) => setChecks((s) => ({ ...s, age: v }))}
                  disabled={submitting}
                />
              }
              label="I confirm I am 18 years old or older."
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={checks.tos}
                  onChange={(_, v) => setChecks((s) => ({ ...s, tos: v }))}
                  disabled={submitting}
                />
              }
              label={
                <Typography variant="body2">
                  I have read and accept the{' '}
                  <Link component={RouterLink} to="/tos" target="_blank" rel="noopener">
                    Terms of Service
                  </Link>
                  .
                </Typography>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={checks.privacy}
                  onChange={(_, v) => setChecks((s) => ({ ...s, privacy: v }))}
                  disabled={submitting}
                />
              }
              label={
                <Typography variant="body2">
                  I have read and accept the{' '}
                  <Link component={RouterLink} to="/privacy" target="_blank" rel="noopener">
                    Privacy Policy
                  </Link>
                  .
                </Typography>
              }
            />
          </Stack>

          <Stack spacing={0.5} sx={{ py: 1, px: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Sessions may be recorded for safety, audit, and legal compliance.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Your messages with peers are stored for safety, abuse prevention, and legal compliance.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Admins may observe live sessions for compliance and safety purposes.
            </Typography>
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}

          <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
            <Link
              variant="caption"
              color="text.secondary"
              component="button"
              type="button"
              onClick={() => navigate('/age-restricted')}
            >
              If you are under 18, click here.
            </Link>
            <Button
              variant="contained"
              size="large"
              disabled={!allChecked || submitting}
              onClick={() => void handleSubmit()}
            >
              {submitting ? 'Submitting...' : 'Continue'}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </AppDialog>
  );
}
